import { gql, useQuery } from '@apollo/client';
import { LoadingSpinner } from 'common/component/LoadingSpinner';
import { ErrorDisplay } from 'common';
import { DEFAULT_PAGE_SIZE, PAGE, REFERRAL_STATUS } from 'placement/const';
import {
    portalReferralParse,
    Referral,
    ReferralColumn,
    SortBy,
    SortOrder
} from 'placement/domains';
import React from 'react';
import {filterValueParse, NavigationFilterValue} from '../domains/NavigationFilterService';
import {ReferralPreview} from './ReferralPreview';
import {ReferralPageAction} from 'placement/reducers/referralPage';
import {CaretDownIcon, CaretUpIcon, Icon} from 'evergreen-ui';
import {FeatureFlaggable, ProviderPortalFeatureFlag, useFeatureFlagFilter} from 'featureflag';

export const REFERRAL_QUERY = gql`
query referralsByStatus($status: [String]!, $first: Int!, $after: String, $sortName: String, $sortDir: String, 
$escalationLevel: [Short], $placementType: [String], $referralConsideration: [String], $matchedProvider: [String]
$language: [String], $city: [String], $serviceType: [String], $hasOfferOnly: Boolean, $offerConversationOnly: Boolean, $serviceLocationType: [String], $counties: [String], $referralSubmittedFrom: LocalDate, $referralSubmittedTo: LocalDate) {
    referralQuery(
    first: $first,
    after: $after,
    params: {
      status: $status,
      sortDir: $sortDir,
      sortName: $sortName,
      escalationLevel: $escalationLevel,
      city: $city,
      placementType: $placementType,
      referralConsideration: $referralConsideration,
      language: $language,
      serviceType: $serviceType,
      hasOfferOnly: $hasOfferOnly,
      offerConversationOnly: $offerConversationOnly,
      matchedProvider: $matchedProvider,
      serviceLocationType: $serviceLocationType,
      counties: $counties,
      referralSubmittedFrom: $referralSubmittedFrom,
      referralSubmittedTo: $referralSubmittedTo
    }){
        edges{
            node{
                guid
                caseNumber
                status
                clientName
                clientAge
                placementType
                serviceLocations
                serviceLocationsParaprofessional
                serviceLocationsMidLevel
                serviceLocationsHighLevel
                serviceLocationsGroupCare
                serviceTypes
                county
                location
                requiredLanguage
                daysInPlacement
                priority
                referralConsiderations
                directCareHours
                midLevelHours
                highLevelHours
                groupCareHours
                scheduleNotes
                escalationLevel
                matchedDate
                completeDate
                clientNotifiedDate
                salesforceCaseUrl
                authDirectCareHours
                authMidLevelHours
                authHighLevelHours
                authGroupCareHours
                authDirectCareBeneficialHours
                authMidLevelBeneficialHours
                authHighLevelBeneficialHours
                authGroupCareBeneficialHours
                careMediatedMidHours
                careMediatedHighHours
                careMediatedMidMinHours
                careMediatedHighMinHours
                practDirectBeneficialHours
                practMidBeneficialHours
                practHighBeneficialHours
                practGroupBeneficialHours
                practDirectOptimalHours
                practMidOptimalHours
                practHighOptimalHours
                practGroupOptimalHours
                authTherapyTreatmentUnitsPer
                authTherapyTreatmentUnitType
                authTherapyTreatmentUnit
                matchedOffer {
                    guid
                    provider {
                        name
                    }
                }
                offerConversationSummaries {
                    offerGuid
                    urgent
                    hasConversation
                    providerUnreadCount
                    clientPlacementTeamUnreadCount
                }
                providerSelected
                clientSiblingReferralsAllProvider {
                    guid
                    siblingCaseNumber
                    clientName
                    siblingReferralGuid
                    clientReferralGuid
                }
            }
        }
        pageInfo{
            startCursor,
            endCursor,
            hasNextPage,
            hasPreviousPage
        }
    }
    referralCount(
        params: {
            status: $status
            escalationLevel: $escalationLevel,
            city: $city,
            placementType: $placementType,
            referralConsideration: $referralConsideration,
            language: $language,
            serviceType: $serviceType,
            hasOfferOnly: $hasOfferOnly,
            offerConversationOnly: $offerConversationOnly,
            matchedProvider: $matchedProvider,
            serviceLocationType: $serviceLocationType,
            counties: $counties,
            referralSubmittedFrom: $referralSubmittedFrom,
            referralSubmittedTo: $referralSubmittedTo
        }
    )
}
`

export const REFERRAL_STATUSES_BY_PAGE: Record<PAGE, Array<REFERRAL_STATUS>> = {
    'Open Referrals': [REFERRAL_STATUS.AVAILABLE, REFERRAL_STATUS.PROVIDER_REQUEST],
    'Matched Providers': [REFERRAL_STATUS.MATCHED],
    'Provider Requests': [REFERRAL_STATUS.AVAILABLE, REFERRAL_STATUS.PROVIDER_REQUEST],
    'Complete Referrals': [REFERRAL_STATUS.COMPLETE],
}

export const OpenReferralColumn: Readonly<ReferralColumn[]>  =  [
    {name: 'Referral ID', attr: 'caseNumber'},
    {name: 'Client Name', attr: 'clientName'},
    {name: 'Age', attr: 'clientAge'},
    {name: 'Scope of Service', attr: 'placementType'},
    {name: 'Service', attr: 'serviceTypes',},
    {name: 'County', attr: 'county',},
    {name: 'Location', attr: 'location'},
    {name: 'Required Language', attr: 'requiredLanguage'},
    {name: 'Days in placement', attr: 'daysInPlacement'},
    {name: 'Escalation Level', attr: 'escalationLevel'}
];

export const MatchedReferralsColumn: Readonly<FeatureFlaggable<ReferralColumn>[]>  = [
    {name: 'Referral ID', attr: 'caseNumber'},
    {name: 'Client Name', attr: 'clientName'},
    {name: 'Age', attr: 'clientAge'},
    {name: 'Scope of Service', attr: 'placementType'},
    {name: 'Service', attr: 'serviceTypes',},
    {name: 'Location', attr: 'location'},
    {name: 'Required Language', attr: 'requiredLanguage'},
    {name: 'Days in placement', attr: 'daysInPlacement'},
    {name: 'Escalation Level', attr: 'escalationLevel'},
    {name: 'Provider Selected', attr: 'matchedOfferProviderName'},
    {name: 'Matched Date', attr: 'matchedDate',},
    {name: 'Communications', attr: 'offerConversationSummaries', featureFlag: ProviderPortalFeatureFlag.BHPNPP_3077_IS_TWO_WAY_MESSAGING_DISABLED, featureFlagNegate: true},
];

export const ProviderRequestColumn: Readonly<FeatureFlaggable<ReferralColumn>[]>  = [
    {name: 'Referral ID', attr: 'caseNumber'},
    {name: 'Client Name', attr: 'clientName'},
    {name: 'Age', attr: 'clientAge'},
    {name: 'Scope of Service', attr: 'placementType'},
    {name: 'Service', attr: 'serviceTypes',},
    {name: 'County', attr: 'county',},
    {name: 'Location', attr: 'location'},
    {name: 'Required Language', attr: 'requiredLanguage'},
    {name: 'Days in placement', attr: 'daysInPlacement'},
    {name: 'Escalation Level', attr: 'escalationLevel'},
    {name: 'Provider Selected', attr: 'matchedOfferProviderName'},
    {name: 'Communications', attr: 'offerConversationSummaries', featureFlag: ProviderPortalFeatureFlag.BHPNPP_3077_IS_TWO_WAY_MESSAGING_DISABLED, featureFlagNegate: true},
];

export const CompletedReferralsColumn: Readonly<FeatureFlaggable<ReferralColumn>[]>  = [
    {name: 'Referral ID', attr: 'caseNumber'},
    {name: 'Client Name', attr: 'clientName'},
    {name: 'Age', attr: 'clientAge'},
    {name: 'Provider Selected', attr: 'matchedOfferProviderName'},
    {name: 'Matched Date', attr: 'matchedDate',},
    {name: 'Client Notification', attr: 'clientNotifiedDate'},
    {name: 'Completed Date', attr: 'completeDate'},
    {name: 'Communications', attr: 'offerConversationSummaries', featureFlag: ProviderPortalFeatureFlag.BHPNPP_3077_IS_TWO_WAY_MESSAGING_DISABLED, featureFlagNegate: true},
];

    type InputParams = {
    pageSize?: number;
    page: PAGE;
    filter?: NavigationFilterValue;
    sortBy: SortBy;
    currentCursor: string | null;
    selectedReferral: Referral | null;
    dispatch: React.Dispatch<ReferralPageAction>;
    referrals: Array<Referral>;
    offerConversationOnly: boolean;
}

export type ReferralQueryResponse = {
    edges: Array<ReferralNode>;
    pageInfo: PageInfo;
}

type ReferralNode = {
    node: Referral
}
type PageInfo = {
    startCursor: string,
    endCursor: string,
    hasNextPage: boolean,
    hasPreviousPage: boolean,
}

export function ReferralList({
    pageSize = DEFAULT_PAGE_SIZE,
    page, sortBy, dispatch, currentCursor, referrals,
    selectedReferral, filter, offerConversationOnly }: InputParams) {


    const variable = {
        first: pageSize,
        after: currentCursor,
        status: REFERRAL_STATUSES_BY_PAGE[page],
        sortDir: sortBy.order,
        sortName: sortBy.column === 'matchedOfferProviderName' ? 'matchedOffer.provider.name' : sortBy.column,
        hasOfferOnly: page === PAGE.PROVIDER_REQUEST,
        offerConversationOnly,
        ...filterValueParse(filter)
    }

    const { loading, data, error } = useQuery<{ referralQuery: ReferralQueryResponse, referralCount: number }, {}>(
        REFERRAL_QUERY, {
        variables: variable,
        fetchPolicy: 'no-cache',
            onCompleted: () => {
            dispatch({
                type: 'SET_FETCH_REFERRAL_COMPLETE',
                pagination: {
                    pageSize: DEFAULT_PAGE_SIZE,
                    startCursor: data!.referralQuery.pageInfo.startCursor,
                    endCursor: data!.referralQuery.pageInfo.endCursor,
                    hasNextPage: data!.referralQuery.pageInfo.hasNextPage,
                    hasPreviousPage: data!.referralQuery.pageInfo.hasPreviousPage,
                    total: data!.referralCount
                },
                referrals: data?.referralQuery.edges.map((edge) => edge.node)!
            });
        },
    }

    );

    const setSortByColumn = (newSortColumn: keyof Referral) => {
        let newSortOrder = SortOrder.DESC;

        // ignore sort by county
        if (newSortColumn === 'county' || newSortColumn === 'offerConversationSummaries') {
            return;
        }

        if (newSortColumn === sortBy.column && sortBy.order === newSortOrder) {
            newSortOrder = SortOrder.ASC;
        }

        dispatch({ type: 'SET_SORT_BY', sortBy: { column: newSortColumn, order: newSortOrder } });
    }

    let columnsMapper;
    switch (page) {
        case PAGE.PROVIDER_REQUEST:
            columnsMapper = ProviderRequestColumn;
            break;
        case PAGE.OPEN_REFERRALS:
            columnsMapper = OpenReferralColumn;
            break;
        case PAGE.COMPLETE_REFERRALS:
            columnsMapper = CompletedReferralsColumn;
            break;
        case PAGE.MATCHED_REFERRALS:
            columnsMapper = MatchedReferralsColumn;
            break;
        default:
            columnsMapper = ProviderRequestColumn;
    }
    const visibleColumns = useFeatureFlagFilter(columnsMapper);

    if (loading) {
        return (
            <LoadingSpinner spacing={20} />
        );
    }

    if (error) {
        return <ErrorDisplay error={error} title="Error loading referrals" />;
    }

    if (data && data.referralQuery.edges.length === 0) {
        return <div className='w-full flex items-center'>
            <div className='m-auto'>
                There are no referrals.
            </div>
        </div>;
    }

    return (
        <React.Fragment>
            <table className='table-auto w-full'>
                <thead>
                    <tr className='text-left select-none text-sm text-gray-portal-table-title'>
                        {
                            visibleColumns.map((column) => (
                                <th
                                    key={column.attr}
                                    className={'font-light' + (column.attr === 'caseNumber' ? ' pl-12' : '')
                                        + (column.attr === 'serviceTypes' ? ' max-w-xs truncate' : '')
                                    }
                                    onClick={() => setSortByColumn(column.attr)}
                                >
                                    <div className='flex items-center'>
                                        <div>
                                            {column.name}
                                        </div>
                                        <div className='w-6 h-6 pl-2'>
                                            {sortBy.column === column.attr && sortBy.order === SortOrder.ASC && <Icon className='align-middle' icon={CaretUpIcon} />}
                                            {sortBy.column === column.attr && sortBy.order === SortOrder.DESC && <Icon className='align-middle' icon={CaretDownIcon} />}
                                        </div>
                                    </div>
                                </th>
                            ))
                        }
                    </tr>
                </thead>
                <tbody>
                    {referrals.map((referral) => (
                        <ReferralPreview key={referral.caseNumber}
                            selected={selectedReferral?.caseNumber === referral.caseNumber}
                            referral={portalReferralParse(referral)}
                            dispatch={dispatch} page={page} />
                    ))}
                </tbody>
            </table>
        </React.Fragment>
    );
}
